import { Link } from "react-router-dom";
import { Award, Heart, Target, Users, ArrowRight } from "lucide-react";

const VALUES = [
  {
    icon: <Heart size={20} />,
    title: "People first",
    desc: "We look at where you're going — not what your credit file says about your past.",
  },
  {
    icon: <Award size={20} />,
    title: "Premium only",
    desc: "Brand-new vehicles, QBE insurance, and a transparent Operating Lease structure.",
  },
  {
    icon: <Target size={20} />,
    title: "Clear path",
    desc: "Defined terms, fixed schedules, full ownership at the end. No moving goalposts.",
  },
  {
    icon: <Users size={20} />,
    title: "Built for ABNs",
    desc: "Designed around the cash-flow realities of Australian tradies, owners and operators.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="relative py-28 md:py-40 overflow-hidden bg-grain">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=2000&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/60 to-[#050505]" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-6 fade-up">About Keys to Own</div>
          <h1
            className="font-display text-white leading-[1.05] fade-up fade-up-delay-1"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", fontWeight: 300 }}
          >
            A different kind of <br />
            <span className="italic text-[#D4AF37]">car ownership story.</span>
          </h1>
          <p className="text-white/70 max-w-2xl mt-8 text-lg leading-relaxed fade-up fade-up-delay-2">
            We started Keys to Own because too many capable Australians were being turned away
            from finance. Brilliant tradies. Hard-working operators. Founders with new ABNs. All
            stuck behind a credit score that didn't tell the real story.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-4">Our Story</div>
            <h2 className="font-display text-4xl md:text-5xl text-white" style={{ fontWeight: 300 }}>
              Built around the realities of Australian business.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/70 leading-relaxed">
            <p>
              Keys to Own was founded on a simple idea: ABN holders deserve a practical, dignified
              way to get behind the wheel of a brand-new vehicle. Not a confusing personal loan with
              hidden fees. Not a worn-out hire car. A clear path to ownership — week by week.
            </p>
            <p>
              We operate across Australia, with active service in Sydney, Melbourne, Brisbane,
              Perth, Adelaide, Canberra, Hobart and Darwin. Every vehicle in our fleet is brand-new,
              under warranty, and ready to put to work the day you collect the keys.
            </p>
            <p>
              Our Operating Lease with deferred purchase option means you rent the vehicle for an
              agreed term, with the option to take full ownership when payments complete. No credit
              checks. No surprises. Just a clear path forward.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-4">What we stand for</div>
          <h2 className="font-display text-4xl md:text-5xl text-white max-w-2xl mb-14" style={{ fontWeight: 300 }}>
            Four principles — every <span className="italic text-[#D4AF37]">single day</span>.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {VALUES.map((v) => (
              <div
                key={v.title}
                data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#0a0a0a] p-10 hover:bg-[#0E0E0E] transition-colors"
              >
                <div className="text-[#D4AF37] mb-6">{v.icon}</div>
                <h3 className="font-display text-2xl text-white mb-3" style={{ fontWeight: 400 }}>
                  {v.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { n: "8", l: "Major cities served" },
            { n: "13.5%", l: "Advance payment" },
            { n: "24–48h", l: "Approval time" },
            { n: "0", l: "Credit checks" },
          ].map((s) => (
            <div key={s.l} data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g, '-')}`}>
              <div
                className="font-display text-5xl md:text-7xl text-[#D4AF37] mb-3"
                style={{ fontWeight: 300 }}
              >
                {s.n}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#080808] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-white text-4xl md:text-5xl" style={{ fontWeight: 300 }}>
            Ready to find your fit?
          </h2>
          <p className="text-white/60 mt-6">
            Submit your application in minutes — our team will be in touch within 24 to 48 hours.
          </p>
          <Link
            to="/apply"
            data-testid="about-cta-apply"
            className="inline-flex mt-10 items-center gap-2 bg-[#D4AF37] text-black px-10 py-4 text-sm tracking-wide font-medium hover:bg-[#F6ECCC] transition-colors"
          >
            Start Application <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
